import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  FaFacebookF,
  FaInstagram,
  FaYoutube,
  FaLinkedinIn,
  FaWhatsapp,
  FaGoogle,
} from "react-icons/fa";

import { FaXTwitter } from "react-icons/fa6";

const footerLinks = [
  { name: "Home", path: "/" },
  { name: "Journey", path: "/journey/" },
  { name: "Photos", path: "/photos/" },
  { name: "Videos", path: "/videos/" },
  {
    name: "Blog",
    path: "https://harisharandevgan.com/blog/",
    external: true,
  },
  { name: "News", path: "/news/" },
  { name: "Connect", path: "/connect/" },
  // STUDENT CASE - FOOTER ITEM
  {
    name: "Student Case",
    path: "/jharkhand-fir-details/",
    translationKey: "footer.submitCase",
  },
];


const socials = [
  {
    name: "Facebook",
    icon: <FaFacebookF size={28} />,
    url: "https://www.facebook.com/Harisharaandevgan/",
  },
  {
    name: "X",
    icon: <FaXTwitter size={28} />,
    url: "https://x.com/harisharandevgn",
  },
  {
    name: "Instagram",
    icon: <FaInstagram size={28} />,
    url: "https://www.instagram.com/harisharandevgan/",
  },
  {
    name: "Youtube",
    icon: <FaYoutube size={28} />,
    url: "https://www.youtube.com/@harisharandevgan",
  },
  {
    name: "LinkedIn",
    icon: <FaLinkedinIn size={28} />,
    url: "https://www.linkedin.com/in/harisharandevgan/",
  },
  {
    name: "WhatsApp",
    icon: <FaWhatsapp size={28} />,
    url: "https://whatsapp.com/channel/0029Vb6kC1aLtOjGkaVl4i1L",
  },
  {
    name: "Google",
    icon: <FaGoogle size={28} />,
    url: "https://profile.google.com/cp/Cg0vZy8xMXEyeHM4YzR2",
  },
];



export default function AdvancedFooter() {
  const { t, i18n } = useTranslation();
  const [instaFeed, setInstaFeed] = useState([]);

  const changeLanguage = (lang) => i18n.changeLanguage(lang);

  useEffect(() => {
    const fetchInstagramPosts = async () => {
      try {
        const response = await fetch(
          "/api/instagram.php"
        );

        const result = await response.json();

        // ✅ Handle backend error
        if (!response.ok || result.error) {
          console.error("Instagram Backend Error:", result.error || result);
          setInstaFeed([]);
          return;
        }

        // ✅ Ensure 'data' exists
        setInstaFeed(result.data || []);
      } catch (error) {
        console.error("Instagram fetch failed:", error);
        setInstaFeed([]);
      }
    };

    fetchInstagramPosts();
  }, []);


  return (
    <footer className="relative z-10 bg-[#e8e8e8] text-gray-900 pt-20 overflow-hidden">
      {/* INSTAGRAM FEED */}
      <div className="px-6 mb-14 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <span className="text-orange-600 font-bold tracking-[0.3em] uppercase text-xs block mb-2">
            Stay Connected
          </span>

          <h2 className="text-4xl md:text-6xl font-black italic uppercase">
            Join me on{" "}
            <a
              href="https://www.instagram.com/harisharandevgan/"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              <span className="text-gray-700 underline decoration-orange-500 underline-offset-8 hover:text-black-600 transition-colors">
                Instagram
              </span>
            </a>

          </h2>

          <p className="mt-4 text-black-600 font-medium">
            <a
              href="https://www.instagram.com/harisharandevgan/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="hover:text-black-600 transition-colors"
            >
              @harisharandevgan
            </a>
          </p>

        </motion.div>

        {/* Instagram Images */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {instaFeed.slice(0, 6).map((post) => (
            <motion.a
              key={post.id}
              href={post.permalink}
              target="_blank"
              rel="nofollow noopener noreferrer"
              whileHover={{ scale: 0.95 }}
              className="aspect-square overflow-hidden relative group"
            >
              <img
                src={
                  post.media_type === "VIDEO"
                    ? post.thumbnail_url
                    : post.media_url
                }
                alt={post.caption || "Instagram"}
                className="w-full h-full object-cover group-hover:grayscale-0 transition-all duration-700"
              />

              <div className="absolute inset-0 bg-orange-500/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <FaInstagram size={26} className="text-white" />
              </div>
            </motion.a>
          ))}
        </div>

      </div>

      {/* FOOTER CONTENT */}
      <div className="px-6 py-12 border-t border-black/10 text-center">
        {/* Social Icons */}
        <div className="flex justify-center items-center gap-6 mb-6">
          {socials.map((social) => (
            <a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.name}
              className="text-black-600 hover:text-gray-900 transition-colors"
            >
              {social.icon}
            </a>
          ))}
        </div>
        {/* Navigation Links */}
        <div className="flex justify-center flex-wrap gap-x-8 gap-y-3 mb-6 text-sm">
          {footerLinks.map((link) =>
            link.external ? (
              <a
                key={link.name}
                href={link.path}            
                target="_blank"           
                rel="nofollow noopener noreferrer"
                className="text-black-600 hover:text-gray-900 transition-colors"
              >
                {t(link.translationKey || `menu.${link.name.toLowerCase()}`)}
              </a>
            ) : (
              <Link
                key={link.name}
                to={link.path}              
                className="text-black-600 hover:text-gray-900 transition-colors"
              >
                {t(link.translationKey || `menu.${link.name.toLowerCase()}`)}
              </Link>
            )
          )}
        </div>
        {/* Copyright */}
        <p className="text-[15px] text-black-600 tracking-widest">
          © 2026 | Harisharan Devgan
        </p>
      </div>
    </footer>
  );
}
